// components/ui/OtpInput.tsx — 6-digit segmented OTP input
// Features: auto-advance, backspace-back, paste-support, shake-on-error, auto-submit,
// a gold glow while the code is being checked, and a checkmark hand-off once verified.

import { useRef, useState, useEffect, type KeyboardEvent, type ClipboardEvent } from 'react';
import styles from './OtpInput.module.css';

// How long the checkmark badge holds on screen before onSuccessComplete
// fires — purely a visual beat for the caller's next step (navigate(),
// advance to the next form step, …); it never affects when that action's
// own backend call happens, only when its UI-facing follow-up runs.
const SUCCESS_HOLD_MS = 700;

function CheckIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        className={styles.checkPath}
        d="M5 13l4 4L19 7"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

interface OtpInputProps {
  length?: number;           // default 6
  onComplete: (otp: string) => void;
  isError?: boolean;         // triggers red + shake animation
  disabled?: boolean;
  autoFocus?: boolean;
  /** True while the caller's own verify call is in flight — pulses a gold
   *  glow across the filled boxes rather than just sitting there disabled. */
  isVerifying?: boolean;
  /** True once verification has actually succeeded — converges the boxes
   *  into a glowing checkmark badge. Never gates the verify call itself,
   *  only this purely visual hand-off; see onSuccessComplete. */
  isSuccess?: boolean;
  /** Fired SUCCESS_HOLD_MS after isSuccess turns true — the caller's cue
   *  to do whatever isSuccess was standing in for (navigate, advance a
   *  step, …), keeping that decision entirely in the caller's hands. */
  onSuccessComplete?: () => void;
}

export function OtpInput({
  length = 6,
  onComplete,
  isError = false,
  disabled = false,
  autoFocus = true,
  isVerifying = false,
  isSuccess = false,
  onSuccessComplete,
}: OtpInputProps) {
  const [values, setValues] = useState<string[]>(Array(length).fill(''));
  const refs = useRef<(HTMLInputElement | null)[]>([]);

  // Auto-focus first box on mount
  useEffect(() => {
    if (autoFocus) refs.current[0]?.focus();
  }, [autoFocus]);

  useEffect(() => {
    if (!isSuccess || !onSuccessComplete) return;
    const t = setTimeout(onSuccessComplete, SUCCESS_HOLD_MS);
    return () => clearTimeout(t);
  }, [isSuccess, onSuccessComplete]);

  // Shake animation reset when error resets
  const [shaking, setShaking] = useState(false);
  useEffect(() => {
    if (isError) {
      setShaking(true);
      setValues(Array(length).fill(''));
      setTimeout(() => { refs.current[0]?.focus(); }, 50);
      const t = setTimeout(() => setShaking(false), 400);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [isError, length]);

  const focusBox = (idx: number) => refs.current[Math.max(0, Math.min(idx, length - 1))]?.focus();

  const handleChange = (idx: number, raw: string) => {
    // Only accept digits
    const digit = raw.replace(/\D/g, '').slice(-1);
    const next = [...values];
    next[idx] = digit;
    setValues(next);

    if (digit) {
      // Advance focus
      if (idx < length - 1) {
        focusBox(idx + 1);
      } else {
        // Last box filled — check every box has a digit
        if (next.every(d => d !== '')) {
          refs.current[idx]?.blur();
          onComplete(next.join(''));
        }
      }
    }
  };

  const handleKeyDown = (idx: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      if (values[idx]) {
        // Clear current
        const next = [...values];
        next[idx] = '';
        setValues(next);
      } else if (idx > 0) {
        // Move back and clear previous
        const next = [...values];
        next[idx - 1] = '';
        setValues(next);
        focusBox(idx - 1);
      }
    } else if (e.key === 'ArrowLeft') {
      focusBox(idx - 1);
    } else if (e.key === 'ArrowRight') {
      focusBox(idx + 1);
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    if (!pasted) return;
    const next = Array(length).fill('');
    pasted.split('').forEach((d, i) => { next[i] = d; });
    setValues(next);
    // Focus last filled box
    const lastFilled = Math.min(pasted.length, length - 1);
    focusBox(lastFilled);
    if (pasted.length === length) {
      onComplete(pasted);
    }
  };

  return (
    // Both the boxes and the checkmark badge stay mounted the whole time,
    // layered via CSS (see .boxesArea/.wrapper/.successWrap), so isSuccess
    // toggling opacity/transform on each is a real crossfade — the boxes
    // converge/fade out while the badge grows/fades in at the same center
    // point — rather than one subtree unmounting and the other mounting in
    // the same tick, which reads as an abrupt cut instead of the
    // reference's hand-off. Title/subtitle for the step this belongs to
    // are the caller's concern (e.g. OtpVerifySheet) — this component is
    // just the box row + its verifying/success states.
    <div className={styles.boxesArea}>
      <div
        className={`${styles.wrapper} ${isSuccess ? styles.wrapperExit : ''}`}
        role="group"
        aria-label="OTP verification code"
        aria-hidden={isSuccess}
      >
        {values.map((v, i) => (
          <span key={i} className={styles.boxSlot}>
            {i === 3 && <span className={styles.separator} aria-hidden="true">–</span>}
            <input
              ref={(el) => { refs.current[i] = el; }}
              className={
                `${styles.box} ${v ? styles.filled : ''} ${(isError || shaking) ? styles.error : ''} ` +
                (isVerifying && v ? styles.glowing : '')
              }
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={1}
              value={v}
              onChange={(e) => handleChange(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              onPaste={handlePaste}
              onFocus={(e) => e.target.select()}
              disabled={disabled || isSuccess}
              tabIndex={isSuccess ? -1 : 0}
              aria-label={`Digit ${i + 1} of ${length}`}
              autoComplete={i === 0 ? 'one-time-code' : 'off'}
            />
          </span>
        ))}
      </div>

      <div
        className={`${styles.successWrap} ${isSuccess ? styles.successEnter : ''}`}
        role="status"
        aria-live="polite"
        aria-hidden={!isSuccess}
      >
        <span className={styles.successBadge}>
          <CheckIcon />
        </span>
      </div>
    </div>
  );
}
